"use client";

/**
 * @fileoverview Provider-free pricing plans for the public marketing site.
 * Reuses the same plan data and RoiHook as the in-app pricing, but without
 * React Query/tRPC/subscription dependencies.
 * CTA buttons link to /signup instead of creating Stripe checkout sessions.
 */

import { motion } from "motion/react";
import Link from "next/link";
import { useRef } from "react";
import { Trans } from "@lingui/react/macro";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { featureFlags } from "@/config/features";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { trackFunnelEvent } from "@/lib/posthog-client";
import { containerVariants, cardVariants, PLANS, type Plan } from "./pricing-data";
import { RoiHook } from "./RoiHook";

function PublicPlanCard({ plan }: { plan: Plan }) {
  // Headline price is the month-to-month price ($29 for Starter); the annual-billed
  // monthly equivalent is shown separately and clearly labeled.
  const monthlyPrice = plan.monthlyPrice;
  const annualMonthlyEquivalent = Math.round(plan.annualPrice / 12);

  return (
    <motion.div variants={cardVariants}>
      <Card
        className={`relative flex h-full flex-col overflow-hidden border ${
          plan.featured
            ? "border-gold-accent bg-gold-accent/5 pt-8 dark:border-gold-accent dark:bg-gold-accent/5"
            : "border-gold-accent/20 bg-white pt-6 dark:border-gold-accent/10 dark:bg-gray-900"
        } shadow-black/5 shadow-md`}
      >
        {plan.featured && (
          <div className="absolute top-0 right-0 left-0 flex items-center justify-center gap-1 bg-gold-accent py-1.5 font-medium text-white text-xs dark:bg-gold-accent">
            <Sparkles className="h-3 w-3" />
            Most Popular
          </div>
        )}
        <CardHeader className="pb-4">
          <h3
            className={`font-semibold text-gray-900 text-xl dark:text-white`}
          >
            {plan.name}
          </h3>
          <div className="flex items-baseline gap-2">
            <span className="font-bold text-4xl text-gray-900 dark:text-white">
              ${monthlyPrice}
            </span>
            <span className="text-gray-500 text-sm dark:text-gray-400">/mo</span>
            <span className="text-red-400 text-sm line-through">
              ${Math.round(monthlyPrice * 1.5)}
            </span>
          </div>
          <p className="text-gray-500 text-xs dark:text-gray-400">
            Or ${annualMonthlyEquivalent}/mo billed annually (${plan.annualPrice}/year)
          </p>
          <p className="mt-1 text-gray-500 text-sm dark:text-gray-400">{plan.description}</p>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-4">
          <ul className="space-y-3">
            {plan.features.map((feature) => (
              <li
                className="flex items-start gap-2 text-gray-900 dark:text-white"
                key={feature}
              >
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-gold-accent dark:text-gold-accent" />
                {feature}
              </li>
            ))}
          </ul>
          <div className="mt-auto pt-4">
            <Button
              asChild
              className={`w-full ${
                plan.featured
                  ? "bg-gold-accent text-white hover:bg-gold-accent/80"
                  : "bg-dark-page text-white hover:bg-dark-page/80 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              }`}
            >
              <Link href={`/signup?plan=${plan.id}`}>
                {featureFlags.enablePaidGate ? (
                  <Trans>Start your free trial</Trans>
                ) : (
                  <Trans>Get started free</Trans>
                )}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/**
 * Public-facing pricing plans for marketing site.
 * No React Query, tRPC, or subscription hooks required.
 * Links CTAs to /signup with plan preselected.
 */
export function PublicPricingPlans() {
  // WS6: Track pricing_viewed on mount (public/unauthenticated surface)
  const hasTracked = useRef(false);
  useMountEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackFunnelEvent("pricing_viewed", {
      source: "public_pricing_page",
      locale: typeof navigator !== "undefined" ? navigator.language : "en",
      authed: false,
      current_tier: "none",
    });
  });

  return (
    <div className="px-4 py-12">
      <div className="mx-auto w-full max-w-5xl">
        <RoiHook />

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
          initial={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <h1
            className={`mb-3 font-bold text-3xl text-gray-900 tracking-tight dark:text-white`}
          >
            Win More Projects. Impress More Clients.
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Look like a $10M firm for less than your coffee budget
          </p>
        </motion.div>

        <motion.div
          animate="visible"
          className="grid gap-6 md:grid-cols-3"
          initial="hidden"
          variants={containerVariants}
        >
          {PLANS.map((plan) => (
            <PublicPlanCard key={plan.id} plan={plan} />
          ))}
        </motion.div>

        <motion.div
          animate={{ opacity: 1 }}
          className="mt-8 space-y-4 text-center"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="space-y-2">
            <p className="font-semibold text-gray-900 dark:text-white">
              {featureFlags.enablePaidGate ? (
                <Trans>Start your free trial</Trans>
              ) : (
                <Trans>Start creating for free</Trans>
              )}
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              {featureFlags.enablePaidGate ? (
                <Trans>Start a 3-day free trial, then $29/month.</Trans>
              ) : (
                <Trans>Start free with 3 renders per day.</Trans>
              )}{" "}
              <Link
                className="font-medium text-gold-accent underline underline-offset-2 hover:text-gold-accent/80 dark:text-gold-accent dark:hover:text-gold-accent/80"
                href="/signup"
              >
                {featureFlags.enablePaidGate ? (
                  <Trans>Subscription required after trial.</Trans>
                ) : (
                  <Trans>No credit card required.</Trans>
                )}
              </Link>
            </p>
          </div>
          <p className="text-gray-500/70 text-sm dark:text-gray-400/70">
            <Trans>Need more?</Trans>{" "}
            <a
              className="font-medium text-gold-accent hover:text-gold-accent/80 dark:text-gold-accent dark:hover:text-gold-accent/80"
              href="mailto:enterprise@aideco.design"
            >
              <Trans>Contact us for Enterprise</Trans>
            </a>
          </p>
          <p className="text-gray-500/60 text-xs dark:text-gray-400/60">
            {featureFlags.enablePaidGate ? (
              <Trans>Cancel anytime. Subscription required after your 3-day free trial.</Trans>
            ) : (
              <Trans>Free forever plan. No credit card required.</Trans>
            )}
          </p>
          <p className="mt-2 font-medium text-amber-600 text-xs dark:text-amber-400">
            <Trans>Lock in founder&apos;s pricing before rates increase.</Trans>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
